// Following a `goto_location` (shared/API.md §36.5) — a command's answer (compose) or a card press's
// (the `goto` signal, DialogHost). In-app paths go through the Shell like every other navigation; a
// path the app has no place for opens `<site URL><path>` in a new tab; other URLs open outside.
import { data } from '../../../bridge/client';
import { CommandOutcome } from '../../../core/commandOutcome';
import { parseRoute } from '../../router';
import type { Shell } from '../../shell/ShellContext';

/** 'unknown' = the router has no place for the path; 'failed' = a known place that did not resolve. */
async function openInApp(shell: Shell, path: string, search: string): Promise<'ok' | 'failed' | 'unknown'> {
  const route = parseRoute(path, search);
  const teamId = (name: string) => shell.teams.find((x) => x.name === name)?.id ?? shell.team.id;
  switch (route.kind) {
    case 'channel': {
      const id = await data.call('channelIdByName', teamId(route.team), route.channelName);
      if (!id) return 'failed';
      shell.openChannel(id);
      return 'ok';
    }
    case 'dm': {
      const user = await data.call('resolveUserByUsername', route.username);
      if (!user) return 'failed';
      shell.openDirectMessage(user.id);
      return 'ok';
    }
    case 'permalink':
      shell.jumpToPost(route.postId);
      return 'ok';
    case 'destination': {
      const d = route.destination;
      if (d === 'search') shell.openSearch(route.param ?? '');
      else if (d === 'threads' || d === 'activity' || d === 'saved' || d === 'drafts') shell.openDestination(d);
      else if (d === 'settings') shell.openSettings(route.param ?? null);
      return 'ok';
    }
    case 'home':
      return 'ok';
    default:
      return 'unknown';
  }
}

/** Follows `location`; false only when an in-app place could not be opened (the caller says so). */
export async function followGoto(shell: Shell | null, location: string, siteUrl: string): Promise<boolean> {
  const target = CommandOutcome.gotoTarget(location, window.location.origin, siteUrl);
  if (!target) return true; // unsafe or empty: ignored (§36.5)
  if (target.kind === 'external') {
    window.open(target.url, '_blank', 'noopener,noreferrer');
    return true;
  }
  if (!shell) return false;
  const opened = await openInApp(shell, target.path, target.search);
  if (opened === 'unknown') {
    const base = (siteUrl || window.location.origin).replace(/\/+$/, '');
    window.open(base + target.path + target.search, '_blank', 'noopener,noreferrer');
    return true;
  }
  return opened === 'ok';
}
